import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  LockOutlined,
  EmailOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';

// Subtle geometric pattern — pure CSS, no image dependency
const GEO_PATTERN = `
  radial-gradient(circle at 15% 50%, rgba(255,255,255,0.04) 0%, transparent 50%),
  radial-gradient(circle at 85% 20%, rgba(255,255,255,0.05) 0%, transparent 40%),
  radial-gradient(circle at 60% 80%, rgba(255,255,255,0.03) 0%, transparent 35%)
`.trim();

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Shared input style — label sits above, subtle gray fill, focus ring
  const inputSx = {
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#374151',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9fafb',
      transition: 'all 0.25s ease',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': {
        borderColor: '#1e3a8a',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Dashboard-matching blue — same gradient family as the sidebar/header
        background: `${GEO_PATTERN}, linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #1e40af 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle geometric rings — replace the muddy illustration */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          width: 480, height: 480,
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '50%', top: -120, right: -120,
        },
        '&::after': {
          content: '""', position: 'absolute',
          width: 320, height: 320,
          border: '1px solid rgba(255,255,255,0.04)',
          borderRadius: '50%', bottom: -80, left: -80,
        },
      }} />
      {/* Extra decorative ring */}
      <Box sx={{
        position: 'absolute', pointerEvents: 'none',
        width: 600, height: 600,
        border: '1px solid rgba(255,255,255,0.03)',
        borderRadius: '50%', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
      }} />

      {/* ── Login Card ── */}
      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          p: 5,
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          zIndex: 1,
          mx: 2,
          animation: 'fadeInUp 0.5s ease-out',
          '@keyframes fadeInUp': {
            from: { opacity: 0, transform: 'translateY(24px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* ── Logo + Brand ── */}
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="Daily2K"
              sx={{ height: 48, width: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.3px' }}>
            Daily2K Admin
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            Marketplace Management Dashboard
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ── Welcome + lock icon ── */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
            <LockIcon sx={{ fontSize: 20, color: '#1e40af' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Welcome Back
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Sign in to access your admin dashboard
          </Typography>
        </Box>

        {/* ── Error alert ── */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5, borderRadius: 2,
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to:   { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* ── Form ── */}
        <form onSubmit={handleSubmit}>
          {/* Email — label above input */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5, fontSize: '0.8rem', letterSpacing: 0.3 }}>
              EMAIL ADDRESS
            </Typography>
            <TextField
              fullWidth
              placeholder="admin@daily2k.ng"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              variant="outlined"
              size="small"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Password — label above input */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5, fontSize: '0.8rem', letterSpacing: 0.3 }}>
              PASSWORD
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              variant="outlined"
              size="small"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* ── Forgot password — right-aligned ── */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
            <Typography
              component="span"
              onClick={() => navigate('/forgot-password')}
              variant="caption"
              sx={{
                color: '#2563eb',
                fontWeight: 600,
                textDecoration: 'none',
                fontSize: '0.8rem',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline', color: '#1e40af' },
              }}
            >
              Forgot password?
            </Typography>
          </Box>

          {/* ── Sign In button — matches dashboard blue exactly ── */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            type="submit"
            disabled={isLoading || !email || !password}
            sx={{
              py: 1.4,
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.4px',
              borderRadius: 2,
              // #1e3a8a matches the admin sidebar/header exactly
              background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              boxShadow: '0 4px 14px rgba(30, 64, 175, 0.35)',
              transition: 'all 0.25s ease',
              '&:hover:not(:disabled)': {
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 20px rgba(30, 64, 175, 0.45)',
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              },
              '&:active:not(:disabled)': {
                transform: 'translateY(0)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #93c5fd 0%, #bfdbfe 100%)',
                color: '#fff',
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: 'white' }} />
                <span>Signing in…</span>
              </Box>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* ── Security badge ── */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            py: 1.2,
            px: 2,
            bgcolor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: 2,
          }}
        >
          <LockIcon sx={{ fontSize: 13, color: '#0369a1' }} />
          <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600, fontSize: '0.72rem', letterSpacing: 0.3 }}>
            Secure Admin Portal — Authorized Personnel Only
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}
